import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Filter, Download, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';
import { seoFor } from '../data/seoMeta';
import { usePricing } from '../lib/pricing';
import { localizeKitName } from '../lib/productName';
import { generatePriceGridPDF } from '../lib/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Catalogue = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const pricing = usePricing(lang);
  const seo = seoFor('/catalogue', lang, { title: `${t.catalogue.title} | Party LED`, description: t.catalogue.subtitle });
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [collectionsRes, productsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/public/collections`),
          axios.get(`${BACKEND_URL}/api/public/products`)
        ]);
        setCollections(collectionsRes.data);
        const sorted = [...productsRes.data].sort((a, b) => a.price - b.price);
        setProducts(sorted);
        setFilteredProducts(sorted);
      } catch (error) {
        toast({ title: "Erreur", description: "Impossible de charger les produits", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category') || 'all';
    const search = params.get('search') || '';

    let filtered = [...products];

    if (category !== 'all') {
      filtered = filtered.filter(p => p.category === category);
      setSelectedCategory(category);
    }

    if (search) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.description.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      filtered.sort((a, b) => a.price - b.price);
    }

    setFilteredProducts(filtered);
  }, [location.search, sortBy, products]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      navigate('/catalogue');
    } else {
      navigate(`/catalogue?category=${category}`);
    }
  };

  const addToCart = (product) => {
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    existingCart.push(product);
    localStorage.setItem('cart', JSON.stringify(existingCart));
    window.dispatchEvent(new Event('cartUpdated'));
    toast({
      title: t.product.added,
      description: product.name,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12 flex items-center justify-center">
        <p className="text-white text-xl">{t.product.loading}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://www.party-led.com/catalogue" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content="https://www.party-led.com/og-image.jpg" />
        <meta property="og:url" content="https://www.party-led.com/catalogue" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }} data-testid="catalogue-title">
              {t.catalogue.title}
            </h1>
            <p className="text-gray-400 text-lg">{t.catalogue.subtitle}</p>
          </div>
          {/* PDF Download Button */}
          <Button
            onClick={() => generatePriceGridPDF(products, lang)}
            variant="outline"
            className="border-purple-600 text-purple-400 hover:bg-purple-600/10 gap-2 shrink-0"
            data-testid="pdf-download-btn"
          >
            <Download className="w-4 h-4" />
            {t.product.pdfDownload}
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => handleCategoryChange('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' ? 'bg-purple-600 hover:bg-purple-700' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
              data-testid="filter-all"
            >
              {t.catalogue.filterAll}
            </Button>
            {collections.map((collection) => (
              <Button
                key={collection.id}
                onClick={() => handleCategoryChange(collection.slug)}
                variant={selectedCategory === collection.slug ? 'default' : 'outline'}
                className={selectedCategory === collection.slug ? 'bg-purple-600 hover:bg-purple-700' : 'border-gray-700 text-gray-300 hover:bg-gray-800'}
              >
                {collection.name}
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px] bg-gray-900 text-white border-gray-700">
                <SelectValue placeholder={t.catalogue.sortLabel} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t.catalogue.sortDefault}</SelectItem>
                <SelectItem value="price-low">{t.catalogue.sortPriceLow}</SelectItem>
                <SelectItem value="price-high">{t.catalogue.sortPriceHigh}</SelectItem>
                <SelectItem value="name">{t.catalogue.sortName}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-gray-900 rounded-lg overflow-hidden group hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300"
                data-testid={`product-card-${product.slug}`}
              >
                <Link to={`/produit/${product.slug}`} className="block relative aspect-square overflow-hidden">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  {product.stock > 0 && product.stock < 10 && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 animate-pulse" data-testid={`low-stock-${product.slug}`}>
                      <AlertTriangle className="w-3 h-3" />
                      {t.product.lowStock}
                    </div>
                  )}
                </Link>

                <div className="p-4">
                  <Link to={`/produit/${product.slug}`}>
                    <h3 className="text-white text-base font-semibold mb-2 group-hover:text-purple-400 transition-colors line-clamp-2">
                      {localizeKitName(product.name, lang)}
                    </h3>
                  </Link>

                  <div className="mb-3">
                    <span className="text-2xl font-bold text-white" data-testid={`catalogue-price-${product.slug}`}>{pricing.fmt(pricing.htOf(product))} HT<span className="block text-gray-100 text-base font-semibold">{pricing.fmt(pricing.ttcOf(product))} TTC</span></span>
                  </div>

                  <Button
                    onClick={() => addToCart(product)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white transition-colors duration-200"
                    data-testid={`add-to-cart-${product.slug}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {t.kits.addToCart}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-400 text-xl">{t.blog.noArticles}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalogue;
